<template>
  <div :class="{'action-button w-[36px] h-[36px] rounded tw-flex items-center justify-center': displayHoverOnActionButton}">
    <div
      class="relative inline-block text-left cursor-pointer"
      :class="{'pointer-events-none': disabled}"
      @click="stopClickPropagation"
    >
      <div @click="toggleDropdown">
        <slot name="button"/>
      </div>

      <div
        v-if="isOpen"
        class="tw-flex flex-col absolute z-10 mt-1 w-auto whitespace-nowrap rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none min-w-[253px] max-w-[253px]"
        :class="classes"
      >
        <template v-if="userInfo">
          <div
            class="tw-flex p-2 border-b border-b-neutral-200 cursor-default"
            @click.stop="stopClickPropagation()"
          >
            <div class="w-7 h-7 rounded-full bg-blue-500 tw-flex items-center justify-center text-white text-sm">
              {{ userInfo.firstName?.[0] || userInfo.name?.[0] || '?' }}
            </div>

            <div class="flex flex-col ml-2">
              <div class="text-[12px]">{{ userInfo.name }}</div>
              <div class="text-[10px] text-neutral-500">{{ userInfo.email }}</div>
            </div>
          </div>
        </template>

        <template v-if="topMenuAction">
          <div
            class="hover:rounded-md dropdown-action__menu-item cursor-pointer"
            :class="getActionVariantClasses(topMenuAction.variant)"
            @click="handleActionClick(topMenuAction)"
          >
            {{ topMenuAction.label }}
          </div>
        </template>

        <template v-if="showActionsTitle">
          <div class="block w-full px-3 mt-2 text-black text-xs h-[20px] font-[500] text-[10px] cursor-default">
            {{ actionsHeaderTitle }}
          </div>
        </template>

        <div :class="{'overflow-y-auto': scrollVariant}">
          <div
            v-for="action in actions"
            :key="action.label"
            class="dropdown-action__menu-item cursor-pointer"
            :class="getActionVariantClasses(action.variant, action.disabled)"
            @click.prevent="handleActionClick(action)"
          >
            <span class="flex-shrink-0 min-w-0 items-center tw-flex">
              <span
                v-if="action.color"
                class="w-3 h-3 rounded-full mr-2"
                :style="{backgroundColor: action.color}"
              ></span>
              <Icon
                v-if="action.icon"
                :name="action.icon"
                class="w-4 h-4 mr-2"
              />
            </span>
            <div class="flex-shrink-0 tw-flex flex-row overflow-ellipsis overflow-hidden whitespace-nowrap w-full justify-between align-middle">
              <span
                class="max-w-[90%] overflow-ellipsis overflow-hidden whitespace-nowrap"
              >
                {{ action.label }}
              </span>
              <span
                v-if="action.tooltipText"
                class="ml-1 text-neutral-300 p-1"
                :title="action.tooltipText"
              >
                <Icon name="info" class="w-3 h-3"/>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from 'vue'
  import Icon from '@/components/Icon/Icon.vue'

  interface DropdownAction {
    label: string
    onClick: () => void
    variant?: 'danger' | 'activeState' | 'default'
    disabled?: boolean
    color?: string
    icon?: string
    tooltipText?: string
  }

  interface UserProfile {
    name: string
    email: string
    firstName?: string
    photoUrl?: string
  }

  export default defineComponent({
    name: 'DropdownActionsButton',
    components: {
      Icon,
    },
    props: {
      actionsHeaderTitle: {
        type: String,
        required: false,
      },
      actions: {
        type: Array as PropType<DropdownAction[]>,
        required: true,
      },
      topMenuAction: {
        type: Object as PropType<DropdownAction>,
        required: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      dropdownExpandXDirection: {
        type: String,
        default: 'left',
        validator(value) {
          return ['left', 'right'].includes(value)
        },
      },
      displayUserInfo: {
        type: Boolean,
        default: false,
      },
      displayHoverOnActionButton: {
        type: Boolean,
        default: false,
      },
      showShortMenuItems: {
        type: Boolean,
        default: false,
      },
      scrollVariant: {
        type: Boolean,
        default: false,
      },
      longVariant: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        isOpen: false
      }
    },
    computed: {
      userInfo(): UserProfile | null {
        if (!this.displayUserInfo) {
          return null
        }

        if (!this.$store.getters.isLoggedIn) {
          return null
        }

        return this.$store.getters.loggedInUser
      },
      showActionsTitle(): boolean {
        return !!(this.actionsHeaderTitle && this.actionsHeaderTitle.length && this.actions.length > 0)
      },
      classes(): string {
        const classNames = []

        if (this.dropdownExpandXDirection === 'left') {
          classNames.push('right-0 origin-top-right')
        } else {
          classNames.push('left-0 origin-top-left')
        }

        if (this.showShortMenuItems) {
          classNames.push('!min-w-[142px] !max-w-[142px]')
        }

        if (this.scrollVariant && !this.longVariant) {
          classNames.push('overflow-hidden h-[135px]')
        }

        if (this.longVariant) {
          classNames.push('max-h-[200px] min-w-[200px]')
        }

        return classNames.join(' ')
      },
    },
    mounted() {
      document.addEventListener('click', this.handleClickOutside)
    },
    beforeUnmount() {
      document.removeEventListener('click', this.handleClickOutside)
    },
    methods: {
      toggleDropdown() {
        this.isOpen = !this.isOpen
      },
      closeDropdown() {
        this.isOpen = false
      },
      handleClickOutside(event) {
        if (!this.$el.contains(event.target)) {
          this.closeDropdown()
        }
      },
      stopClickPropagation(e) {
        e?.stopPropagation()
      },
      handleActionClick(action: DropdownAction) {
        if (!action.disabled && action.onClick) {
          action.onClick()
          this.closeDropdown()
        }
      },
      getActionVariantClasses(variantType?: string, isDisabled = false): string {
        if (isDisabled) {
          return 'bg-neutral-100 text-neutral-300 cursor-not-allowed'
        }

        switch (variantType) {
          case 'danger':
            return 'bg-red-50 text-red-600 hover:bg-red-100'
          case 'activeState':
            return 'bg-primary-50 text-primary-600'
          default:
            return 'hover:bg-gray-50 hover:text-primary-600 text-gray-700'
        }
      },
    },
  })
</script>

<style lang="scss" scoped>
  .dropdown-action__menu-item {
    @apply flex flex-row flex-shrink-0 justify-start items-center w-full h-[35px] px-3 text-left overflow-hidden;

    &:not(.cursor-not-allowed):hover {
      @apply transition-colors duration-150;
    }
  }

  .action-button:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
</style>
